// services/paddleService.ts

import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

export interface PaddleClientConfiguration {
    clientToken: string;
    environment: 'sandbox' | 'production';
    extraClientPriceId: string;
    portalUrl: string;
}

let clientConfigurationPromise: Promise<PaddleClientConfiguration> | null = null;

export const getPaddleClientConfiguration = (): Promise<PaddleClientConfiguration> => {
    if (clientConfigurationPromise)
        return clientConfigurationPromise;
    clientConfigurationPromise = (async () => {
        const functions = getFunctions(getApp(), 'us-central1');
        const callable = httpsCallable(functions, 'getPaddleClientConfiguration');
        return (await callable({})).data as PaddleClientConfiguration;
    })().catch(error => {
        clientConfigurationPromise = null;
        throw error;
    });
    return clientConfigurationPromise;
};

export interface PaddleTierPriceIds {
    individual: string;
    business: string;
    agency: string;
    annualIndividual: string;
    annualBusiness: string;
    annualAgency: string;
}

let priceMappingsPromise: Promise<PaddleTierPriceIds> | null = null;

export const getPaddleTierPriceIds = (): Promise<PaddleTierPriceIds> => {
    if (priceMappingsPromise)
        return priceMappingsPromise;
    priceMappingsPromise = (async () => {
        const functions = getFunctions(getApp(), 'us-central1');
        const callable = httpsCallable(functions, 'getPaddlePriceMappings');
        const response = await callable({});
        const mappings = (response.data as any)?.mappings || {};
        return {
            individual: mappings.individual?.monthlyPriceId || '',
            business: mappings.business?.monthlyPriceId || '',
            agency: mappings.agency?.monthlyPriceId || '',
            annualIndividual: mappings.individual?.annualPriceId || '',
            annualBusiness: mappings.business?.annualPriceId || '',
            annualAgency: mappings.agency?.annualPriceId || '',
        };
    })().catch(error => {
        priceMappingsPromise = null;
        throw error;
    });
    return priceMappingsPromise;
};

declare global {
    interface Window {
        Paddle: any;
    }
}

let initPromise: Promise<void> | null = null;

export const initializePaddle = (): Promise<void> => {
    if (initPromise)
        return initPromise;
    initPromise = (async () => {
        if (!window.Paddle) {
            console.warn("❌ Paddle script not loaded.");
            throw new Error('Paddle script not loaded.');
        }

        const configuration = await getPaddleClientConfiguration();
        if (!configuration.clientToken)
            throw new Error('Paddle client token missing.');

        try {
            if (configuration.environment === 'sandbox')
                window.Paddle.Environment.set('sandbox');

            window.Paddle.Initialize({
                token: configuration.clientToken,
                eventCallback: (data: any) => {
                    if (configuration.environment === 'sandbox' || import.meta.env.DEV)
                        console.log('Paddle Global Event:', data.name, data);
            
                    if (data.name === 'checkout.completed') {
                        console.log('Checkout completed.');
                        window.Paddle.Checkout.close();
                        if (!window.dispatchEvent(new CustomEvent('inteloptics:paddle-checkout-completed', { detail: data, cancelable: true })))
                            return;
                        const path = window.location.pathname.replace(/\/+$/, '') || '/';
                        if (path === '/checkout' || path === '/join' || path === '/msa') {
                            return;
                        }
                        window.location.href = '/dashboard?checkout=success';
                    }
                }
            });
        } catch (e) {
            console.error("Paddle Initialization Failed:", e);
            throw e;
        }
    })().catch(error => {
        initPromise = null;
        throw error;
    });

    return initPromise;
};

export interface PaddleCheckoutOptions {
    items?: Array<{ priceId: string; quantity: number }>; // Optional for billing updates
    subscriptionId?: string; // FIX for error 2353 at line 106
    customerId?: string;
    addressId?: string;
    businessId?: string;
    customer?: { 
        email: string; 
        address?: { countryCode: string; postalCode?: string };
    };
    customData?: Record<string, any>; 
    discountId?: string;
    settings?: any;
    // FIX for error 2353 at line 78
    events?: {
        onTransactionSuccess?: (transaction: any) => void; // Added 'any' to fix 7006
        onTransactionError?: (error: any) => void;       // Added 'any' to fix 7006
        onCheckoutClose?: () => void;
        onCheckoutOpen?: () => void;
    };
}

export const openCheckout = async (options: PaddleCheckoutOptions) => {
    await initializePaddle();

    // 1. PREPARE CUSTOM DATA
    const customData = options.customData;

    if (!customData || (!customData.organization_id && !customData.msa_application_id)) {
        console.warn("⚠️ PaddleService: Missing organization_id in customData. Checkout may fail to link account.");
    }

    const cleanPaddleOptions: any = {
        // 1. Only include items if they are provided (prevents errors on billing updates)
        ...(options.items && { items: options.items }),
        
        // 2. Map subscriptionId if present (required for "Manage Billing" via Checkout)
        ...(options.subscriptionId && { subscriptionId: options.subscriptionId }),
        
        // 3. Map discountId if present
        ...(options.discountId && { discountId: options.discountId }),

        settings: {
            displayMode: 'overlay',
            theme: 'light',
            ...options.settings
        },

        /**
         * FIX: Paddle v2 SDK expects 'customData' (camelCase).
         */
        customData: customData
    };

    // 2. HANDLE CUSTOMER OBJECT
    if (options.customerId) {
        cleanPaddleOptions.customerId = options.customerId;
    } else if (options.customer?.email) {
        cleanPaddleOptions.customer = { 
            email: options.customer.email 
        };

        // 3. POSTAL CODE FIX
        // Only include address if BOTH country and zip exist to avoid Paddle validation errors
        const country = options.customer.address?.countryCode;
        const zip = options.customer.address?.postalCode;

        if (!options.addressId && country && zip) {
            cleanPaddleOptions.customer.address = {
                countryCode: country,
                postalCode: zip
            };
        }
    } // <--- Added missing closing brace for the "else if" block

    // 4. HANDLE OTHER IDS
    if (options.addressId) cleanPaddleOptions.addressId = options.addressId;
    if (options.businessId) cleanPaddleOptions.businessId = options.businessId;

    try {
        window.Paddle.Checkout.open(cleanPaddleOptions);
    } catch (e) {
        console.error("Checkout Failed:", e);
        throw e;
    }
};

export const openCustomerPortal = async (customerId: string) => {
    if (!customerId) {
        console.warn("⚠️ openCustomerPortal: No customer ID provided.");
        alert("No billing account linked yet. Please contact support if this persists.");
        return;
    }

    try {
        console.log(`Generating authenticated Paddle Customer Portal session for ${customerId}...`);
        
        // Use Firebase Callable Function to execute API call securely.
        // This generates a single-use token URL, completely bypassing the public Turnstile CAPTCHA.
        const functions = getFunctions(getApp(), "us-central1");
        const createPortalSession = httpsCallable(functions, "createPortalSession");
        
        const res = await createPortalSession({ customerId });
        const portalUrl = (res.data as any)?.urls?.general?.overview;

        if (portalUrl) {
            console.log("✅ Successfully generated portal URL. Opening in new tab...");
            window.open(portalUrl, '_blank');
        } else {
            console.error("No portal URL returned from backend.", res.data);
            alert("No portal URL returned. Please check system logs.");
        }
    } catch (e: any) {
        console.error("Failed to generate authenticated Paddle Customer Portal:", e);
        
        let errorMessage = "Failed to open billing portal.";
        
        if (e.message?.includes("not defined")) {
            errorMessage = "Billing configuration is incomplete. PADDLE_API_KEY is missing on the server.";
        } else if (e.details?.error?.detail) {
            errorMessage = `Paddle Error: ${e.details.error.detail}`;
        } else if (e.message) {
            errorMessage = e.message;
        }

        console.error("Custom Portal Error Details:", {
            message: e.message,
            details: e.details,
            code: e.code
        });

        // Fallback or Alert
        if (errorMessage.includes("Paddle API Error")) {
            alert(errorMessage + "\n\nPlease ensure your account is fully set up in Paddle.");
        } else {
            console.warn("Falling back to public portal due to API failure.");
            window.open((await getPaddleClientConfiguration()).portalUrl, '_blank');
        }
    }
};
